import { InjectableService } from '@/shared/services/InjectableService'
import type { AxiosResponse } from 'axios'
import { submitApiRequest, HttpMethod } from '@/services/aplosApi'
import type { LegacyUser } from '../models/LegacyUser'
import type { LegacyTenant } from '../models/LegacyTenant'
import type { LegacySubscription } from '../models/LegacySubscription'
import type { LegacyFile } from '../models/LegacyFile'
import type { LegacyExecution } from '../models/LegacyExecution'
import type {
  LegacyUserListResponse,
  LegacyTenantListResponse,
  LegacySubscriptionListResponse,
  LegacyFileListResponse,
  LegacyExecutionListResponse,
} from '../models/LegacyPaginatedResponse'

/** Base path for all legacy v2 API endpoints */
const LEGACY_PREFIX = '/v2'

/**
 * Builds the tenant-scoped base path for legacy endpoints.
 */
function getLegacyTenantPath(tenantId: string): string {
  return `${LEGACY_PREFIX}/tenants/${tenantId}`
}

/**
 * Builds a query string with optional start_key for pagination.
 * Returns empty string when no startKey is provided.
 */
function buildPaginationQuery(startKey?: string): string {
  if (!startKey) return ''
  return `?start_key=${encodeURIComponent(startKey)}`
}

export interface ILegacyApiService {
  listAllUsers(sortBy?: string, startKey?: string): Promise<AxiosResponse<LegacyUserListResponse>>
  searchUsers(field: 'email' | 'last_name' | 'first_name', query: string, startKey?: string): Promise<AxiosResponse<LegacyUserListResponse>>
  listUsers(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacyUserListResponse>>
  getUser(tenantId: string, userId: string): Promise<AxiosResponse<LegacyUser>>
  listTenants(startKey?: string): Promise<AxiosResponse<LegacyTenantListResponse>>
  searchTenants(query: string, startKey?: string): Promise<AxiosResponse<LegacyTenantListResponse>>
  getTenant(tenantId: string): Promise<AxiosResponse<LegacyTenant>>
  listSubscriptions(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacySubscriptionListResponse>>
  getSubscription(tenantId: string, subscriptionId: string): Promise<AxiosResponse<LegacySubscription>>
  listFiles(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacyFileListResponse>>
  listUserFiles(tenantId: string, userId: string, startKey?: string): Promise<AxiosResponse<LegacyFileListResponse>>
  getFile(tenantId: string, userId: string, fileId: string): Promise<AxiosResponse<LegacyFile>>
  listExecutions(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacyExecutionListResponse>>
  listUserExecutions(tenantId: string, userId: string, startKey?: string): Promise<AxiosResponse<LegacyExecutionListResponse>>
  getExecution(tenantId: string, userId: string, executionId: string): Promise<AxiosResponse<LegacyExecution>>
}

export class LegacyApiService extends InjectableService implements ILegacyApiService {

  static readonly injectableKey = 'ILegacyApiService'

  get injectableKey() {
    return (this.constructor as typeof LegacyApiService).injectableKey
  }

  // --- All Users (cross-tenant) ---

  async listAllUsers(sortBy?: string, startKey?: string): Promise<AxiosResponse<LegacyUserListResponse>> {
    const params: string[] = []
    if (sortBy) params.push(`sort_by=${encodeURIComponent(sortBy)}`)
    if (startKey) params.push(`start_key=${encodeURIComponent(startKey)}`)
    const qs = params.length ? `?${params.join('&')}` : ''
    return await submitApiRequest(HttpMethod.GET, `${LEGACY_PREFIX}/users${qs}`)
  }

  async searchUsers(field: 'email' | 'last_name' | 'first_name', query: string, startKey?: string): Promise<AxiosResponse<LegacyUserListResponse>> {
    const params: string[] = [
      `field=${encodeURIComponent(field)}`,
      `query=${encodeURIComponent(query)}`,
    ]
    if (startKey) params.push(`start_key=${encodeURIComponent(startKey)}`)
    const qs = `?${params.join('&')}`
    return await submitApiRequest(HttpMethod.GET, `${LEGACY_PREFIX}/users/search${qs}`)
  }

  // --- Users ---

  async listUsers(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacyUserListResponse>> {
    const basePath = getLegacyTenantPath(tenantId)
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/users${qs}`)
  }

  async getUser(tenantId: string, userId: string): Promise<AxiosResponse<LegacyUser>> {
    const basePath = getLegacyTenantPath(tenantId)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/users/${userId}`)
  }

  // --- Tenants ---

  async listTenants(startKey?: string): Promise<AxiosResponse<LegacyTenantListResponse>> {
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${LEGACY_PREFIX}/tenants${qs}`)
  }

  async searchTenants(query: string, startKey?: string): Promise<AxiosResponse<LegacyTenantListResponse>> {
    const params: string[] = [`query=${encodeURIComponent(query)}`]
    if (startKey) params.push(`start_key=${encodeURIComponent(startKey)}`)
    const qs = `?${params.join('&')}`
    return await submitApiRequest(HttpMethod.GET, `${LEGACY_PREFIX}/tenants/search${qs}`)
  }

  async getTenant(tenantId: string): Promise<AxiosResponse<LegacyTenant>> {
    return await submitApiRequest(HttpMethod.GET, `${LEGACY_PREFIX}/tenants/${tenantId}`)
  }

  // --- Subscriptions ---

  async listSubscriptions(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacySubscriptionListResponse>> {
    const basePath = getLegacyTenantPath(tenantId)
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/subscriptions${qs}`)
  }

  async getSubscription(tenantId: string, subscriptionId: string): Promise<AxiosResponse<LegacySubscription>> {
    const basePath = getLegacyTenantPath(tenantId)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/subscriptions/${subscriptionId}`)
  }

  // --- Files ---

  async listFiles(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacyFileListResponse>> {
    const basePath = getLegacyTenantPath(tenantId)
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/files${qs}`)
  }

  async listUserFiles(tenantId: string, userId: string, startKey?: string): Promise<AxiosResponse<LegacyFileListResponse>> {
    const basePath = getLegacyTenantPath(tenantId)
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/users/${userId}/files${qs}`)
  }

  async getFile(tenantId: string, userId: string, fileId: string): Promise<AxiosResponse<LegacyFile>> {
    const basePath = getLegacyTenantPath(tenantId)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/users/${userId}/files/${fileId}`)
  }

  // --- Executions ---

  async listExecutions(tenantId: string, startKey?: string): Promise<AxiosResponse<LegacyExecutionListResponse>> {
    const basePath = getLegacyTenantPath(tenantId)
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/executions${qs}`)
  }

  async listUserExecutions(tenantId: string, userId: string, startKey?: string): Promise<AxiosResponse<LegacyExecutionListResponse>> {
    const basePath = getLegacyTenantPath(tenantId)
    const qs = buildPaginationQuery(startKey)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/users/${userId}/executions${qs}`)
  }

  async getExecution(tenantId: string, userId: string, executionId: string): Promise<AxiosResponse<LegacyExecution>> {
    const basePath = getLegacyTenantPath(tenantId)
    return await submitApiRequest(HttpMethod.GET, `${basePath}/users/${userId}/executions/${executionId}`)
  }
}
